import { Injectable, inject } from '@angular/core';
import { areBoxesOverlapping } from '@functions';
import {
  CanvasService,
  DetectionService,
  LogicService,
  MessageService,
  RenderService,
  SimulationService,
} from '@services';
import '@tensorflow/tfjs-backend-webgl';
import { getMiddleLeg, getSortedLegs } from './ligating-loop-helpers';
import { LigatingLoopMetricsService } from './ligating-loop-metrics.service';

const CONFIDENCE_LOOP = 0.2;

@Injectable({
  providedIn: 'root',
})
export class SharedService {
  private canvasService = inject(CanvasService);
  private ligatingLoopMetricsService = inject(LigatingLoopMetricsService);
  private detectionService = inject(DetectionService);
  private logicService = inject(LogicService);
  private messageService = inject(MessageService);
  private renderService = inject(RenderService);
  private simulationService = inject(SimulationService);

  cycleWorkerId: string = undefined;
  knotWorkerId: string = undefined;

  tightenLoopState = {
    loopClosed: false,
  };

  loopData = {
    isLoopDetected: false,
    loopOccurences: [] as number[],
  };

  checkSample(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const legs = getSortedLegs(
          this.detectionService.getAllBoxInstances('leg', this.cycleWorkerId),
        );

        this.messageService.showDetectionMessage({
          text: 'Place foam organ in the clip in the center of the camera view',
          identifier: 'positionFoam',
        });

        const target = this.canvasService.convertBoxCoordinates({
          width: 640,
          height: 400,
          moveX: -100,
          moveY: 150,
        });

        this.logicService.checkSamplePosition({
          sample: 'leg',
          workerId: this.cycleWorkerId,
          confidence: 0.5,
          width: 640,
          height: 400,
          moveX: -100,
          moveY: 150,
          image: 'overlays/foam-organ.svg',
          additionalCondition:
            legs[0]?.min.x > target.min.x &&
            legs[2]?.max.x < target.max.x &&
            legs[2]?.max.y < target.max.y,
        });

        resolve(this.simulationService.state() === 'simulation');
      } catch (error) {
        reject(error);
      }
    });
  }

  checkInstrument(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        this.logicService
          .checkInstrumentsPosition([
            {
              id: 'grasper',
              workerId: this.cycleWorkerId,
              confidence: 0.2,
              moveX: -300,
              radius: 90,
              image: 'overlays/instrument-left.svg',
            },
          ])
          .then((instrumentConfirmed) => {
            if (!instrumentConfirmed) {
              this.messageService.showDetectionMessage({
                text: 'Bring your instrument in the camera view',
                identifier: 'checkInstrument',
              });
            }
            resolve(instrumentConfirmed);
          });
      } catch (error) {
        reject(error);
      }
    });
  }

  graspLeg(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const grasper = this.detectionService.getBox(
          'grasper',
          this.cycleWorkerId,
        );

        const middleLeg = getMiddleLeg(
          this.detectionService.getAllBoxInstances('leg', this.cycleWorkerId),
        );

        this.messageService.showDetectionMessage({
          text: 'Grasp the middle leg on the bottom',
          identifier: 'graspFoam',
        });

        if (middleLeg) {
          this.renderService.addDynamic({
            overlay: {
              type: 'image',
              image: 'overlays/foam-grasp-loop.svg',
              x: Math.round(middleLeg.center.x - 150),
              y: Math.round(middleLeg.center.y),
            },
          });
        }

        const isLegGrasped =
          middleLeg && grasper ? areBoxesOverlapping(middleLeg, grasper) : null;

        const isLoopAround =
          this.detectionService.getNumberOfBoxOccurencies(
            'loopInside',
            this.knotWorkerId,
            9,
          ) > 5;

        const isObjectGraspedAndLoopInside = isLegGrasped && isLoopAround;

        resolve(isObjectGraspedAndLoopInside);
      } catch (error) {
        reject(error);
      }
    });
  }

  tightenLoop(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const legs = this.detectionService.getAllBoxInstances(
          'leg',
          this.cycleWorkerId,
        );

        const middleLeg = getMiddleLeg(legs);

        this.messageService.showDetectionMessage({
          text: 'Tighten the loop while keeping the knot positioned at the mark of the foam organ',
          identifier: 'breakEndLoop',
        });

        if (middleLeg) {
          this.renderService.addDynamic({
            overlay: {
              type: 'image',
              image: 'overlays/tighten-loop.svg',
              x: Math.round(middleLeg.center.x + 200),
              y: Math.round(middleLeg.center.y),
            },
          });
        }

        const isKnotTight =
          this.detectionService.getNumberOfBoxOccurencies(
            'knotTight',
            this.knotWorkerId,
            9,
          ) > 3;

        const knotOccured =
          this.detectionService.getNumberOfBoxOccurencies(
            'knot',
            this.cycleWorkerId,
            5,
          ) > 3;

        const isKnotConfirmed = isKnotTight && knotOccured;

        resolve(isKnotConfirmed);
      } catch (error) {
        reject(error);
      }
    });
  }

  async cutThread(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        const middleLeg = getMiddleLeg(
          this.detectionService.getAllBoxInstances('leg', this.cycleWorkerId),
        );

        if (middleLeg) {
          this.renderService.addDynamic({
            overlay: {
              type: 'image',
              image: 'overlays/cut-suture.svg',
              x: middleLeg.center.x + 200,
              y: middleLeg.center.y,
            },
          });
        }

        resolve(false);
      } catch (error) {
        reject(error);
      }
    });
  }

  updateObjects() {
    const legs = this.detectionService.getAllBoxInstances(
      'leg',
      this.cycleWorkerId,
    );
    const loop = this.detectionService.getBox('loop', this.cycleWorkerId);
    this.loopData.loopOccurences.shift();
    this.loopData.loopOccurences.push(
      legs && loop
        ? legs.length > 2 &&
          loop.confidence > CONFIDENCE_LOOP &&
          loop.max.y > 0.5 * getMiddleLeg(legs).min.y
          ? 1
          : 0
        : null,
    );
  }

  toolTracking() {
    this.ligatingLoopMetricsService.trackGrasper(
      this.detectionService.getBox('grasper', this.cycleWorkerId),
    );
    this.ligatingLoopMetricsService.trackLoop(
      this.detectionService.getBox('ligatingTool', this.cycleWorkerId),
    );
  }
}
